<template>
  <Teleport to="body">
    <div class="howto-backdrop mix-reveal-backdrop" @click.self="skipOrClose">
      <div class="mix-reveal" :class="[`mix-reveal--${stage}`, { 'mix-reveal--eureka': exp.eureka, 'mix-reveal--failed': exp.failed }]" role="dialog" aria-modal="true" aria-label="The experiment" @click="skipOrClose">
        <span class="mix-reveal__kind">The experiment</span>
        <!--
          THE EXPERIMENT AS A SCENE (playtest 11, Matt: the old pour - a
          streak, a disc and a rod in a box - "is total garbage, you gotta
          try again"). One flask. The two elements arc in from either side
          and drop into its neck; the liquid rises in their two colours and
          bubbles; then what they share floats up out of the neck, one
          property at a time. A failed bet goes grey and smokes; a eureka
          flares gold. All SVG, so it is the same at any size.
        -->
        <div class="mx" :class="[`mx--${stage}`, { 'mx--failed': exp.failed, 'mx--eureka': exp.eureka }]" aria-hidden="true">
          <svg class="mx__scene" viewBox="0 0 240 170">
            <defs>
              <clipPath :id="`mx-clip-${uid}`">
                <path d="M106 34 V70 L72 128 A16 16 0 0 0 86 152 H154 A16 16 0 0 0 168 128 L134 70 V34 Z" />
              </clipPath>
              <linearGradient :id="`mx-liquid-${uid}`" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" :stop-color="colourA" />
                <stop offset="1" :stop-color="colourB" />
              </linearGradient>
              <radialGradient :id="`mx-glow-${uid}`">
                <stop offset="0" :stop-color="exp.eureka ? '#f4c45a' : '#b7a6ff'" stop-opacity="0.55" />
                <stop offset="1" :stop-color="exp.eureka ? '#f4c45a' : '#b7a6ff'" stop-opacity="0" />
              </radialGradient>
            </defs>
            <!-- the glow behind the flask while it works -->
            <circle class="mx__glow" cx="120" cy="112" r="78" :fill="`url(#mx-glow-${uid})`" />
            <!-- the liquid, clipped to the flask: it rises, then sways -->
            <g :clip-path="`url(#mx-clip-${uid})`">
              <g class="mx__liquid">
                <path class="mx__surface" d="M40 0 Q70 -6 100 0 T160 0 T220 0 V90 H40 Z" :fill="`url(#mx-liquid-${uid})`" />
              </g>
              <circle v-for="n in 6" :key="n" class="mx__bubble" :cx="92 + n * 9" cy="150" :r="2 + (n % 3)" :style="{ '--i': n }" />
            </g>
            <!-- the glass itself, drawn over the liquid -->
            <path class="mx__glass" d="M106 34 V70 L72 128 A16 16 0 0 0 86 152 H154 A16 16 0 0 0 168 128 L134 70 V34" />
            <path class="mx__lip" d="M100 34 H140" />
            <path class="mx__shine" d="M84 132 Q90 120 98 108" />
            <!-- smoke, for a reaction that fails -->
            <circle v-for="n in 3" :key="`s${n}`" class="mx__smoke" :cx="112 + n * 6" cy="30" :r="7 + n * 2" :style="{ '--i': n }" />
          </svg>
          <!-- the two elements, arcing in from either side -->
          <span class="mx__drop mx__drop--a" :style="{ '--c': colourA }">
            <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
            <svg viewBox="0 0 24 24" v-html="artA" />
          </span>
          <span class="mx__drop mx__drop--b" :style="{ '--c': colourB }">
            <!-- eslint-disable-next-line vue/no-v-html -- constant art from loreArt.js -->
            <svg viewBox="0 0 24 24" v-html="artB" />
          </span>
          <span class="mx__names">{{ nameA }} + {{ nameB }}</span>
        </div>
        <div class="mix-reveal__glass">
          <span v-if="stage === 'ready' || stage === 'pour' || stage === 'brew'" class="mix-reveal__wait">{{ stage === 'pour' ? 'into the flask…' : 'it bubbles…' }}</span>
          <template v-else>
            <strong v-if="exp.failed" class="mix-reveal__said">the reaction fails</strong>
            <strong v-else-if="!shared.length" class="mix-reveal__said">nothing in common</strong>
            <span v-else class="mix-reveal__badges">
              <span class="mix-reveal__both">both</span>
              <span v-for="(k, i) in shown" :key="k" class="mix-reveal__badge" :style="{ '--i': i }">
                <Aspect :aspect="k" :value="exp.shared[k]" />
              </span>
            </span>
          </template>
        </div>
        <p v-if="stage === 'done' && exp.eureka" class="mix-reveal__eureka">EUREKA · {{ techName }} is yours</p>
        <!-- What it does, and what it unlocks (Matt, playtest 10: "made clear
             to me that now I have access to something new on the tree"). -->
        <p v-if="stage === 'done' && exp.eureka && does" class="mix-reveal__note">{{ does }}</p>
        <p v-if="stage === 'done' && exp.eureka" class="mix-reveal__note mix-reveal__opens">{{ opens.length ? `Opens on the tree: ${opens.join(', ')}.` : 'Nothing new opens on the tree from this one.' }}</p>
        <p v-else-if="stage === 'done' && exp.failed" class="mix-reveal__note">So they are not both {{ need }}.</p>
        <p v-else-if="stage === 'done'" class="mix-reveal__note">{{ lesson }}</p>
        <button v-if="stage === 'done'" class="primary mix-reveal__go" @click.stop="$emit('close')">Got it</button>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ASPECTS, PRINCIPLE_NAMES, ALL_PRINCIPLES, aspectWord } from '@/engine/lore';
import { prefersReducedMotion } from '@/utils/eventTheatre';
import { artForPrinciple } from './loreArt';
import Aspect from './Aspect.vue';

// A MIX GETS ITS MOMENT (Seth, playtest 9: "it was not clear when I clicked
// mix that it worked - I needed something to pop up to tell me it had been
// accepted"; Matt: "in addition to it popping up it should be cool and
// dramatic... build the drama"). The engine has already settled it; this
// only paces it, house theatre rules: the two go in, the glass bubbles,
// then what they share comes up one property at a time, the big news last.
// A tap skips to the end; reduced motion starts there.
const POUR_MS = 1250;
const BREW_MS = 1500;
const EACH_MS = 420;
// One colour per element, eight for each bench.
const COLOURS = ['#e8673c', '#3f8fd8', '#9a8f7c', '#9fd0e6', '#5fb35a', '#b8c0c8', '#d8b542', '#c46fb5'];
const colourOf = (p) => {
  const i = ALL_PRINCIPLES.indexOf(p);
  return COLOURS[(i < 0 ? 0 : i) % COLOURS.length];
};

export default {
  name: 'MixReveal',
  components: { Aspect },
  props: {
    // { a, b, shared, target, eureka, failed }
    exp: { type: Object, required: true },
    techName: { type: String, default: '' },
    need: { type: String, default: '' },
    // On a eureka: what the technology does, and which ones it opens.
    does: { type: String, default: '' },
    opens: { type: Array, default: () => [] },
  },
  emits: ['close'],
  data() {
    return { stage: 'ready', count: 0, timers: [], uid: Math.random().toString(36).slice(2, 8) };
  },
  computed: {
    nameA() { return PRINCIPLE_NAMES[this.exp.a] || this.exp.a; },
    nameB() { return PRINCIPLE_NAMES[this.exp.b] || this.exp.b; },
    artA() { return artForPrinciple(this.exp.a); },
    artB() { return artForPrinciple(this.exp.b); },
    // Each element has its own colour in the flask, so the mix is two things.
    colourA() { return colourOf(this.exp.a); },
    colourB() { return colourOf(this.exp.b); },
    shared() { return ASPECTS.filter((k) => k in (this.exp.shared || {})); },
    shown() { return this.stage === 'done' ? this.shared : this.shared.slice(0, this.count); },
    // Name the other traits too (Matt, playtest 10: "opposite on the rest -
    // I'm not sure it's clear what that means").
    lesson() {
      const pair = (k) => `${aspectWord(k, true)} and the other ${aspectWord(k, false)}`;
      const rest = ASPECTS.filter((k) => !this.shared.includes(k));
      if (!this.shared.length) return `${this.nameA} and ${this.nameB} are opposites on all three: one is ${rest.map(pair).join('; one ')}.`;
      const words = this.shared.map((k) => aspectWord(k, this.exp.shared[k])).join(' and ');
      if (!rest.length) return `Both are ${words}: the same on all three. Mark it in your notebook.`;
      return `Both are ${words}. On the ${rest.length === 1 ? 'other trait' : 'other two'} they differ: one is ${rest.map(pair).join('; one ')}. Mark it in your notebook.`;
    },
  },
  mounted() {
    if (prefersReducedMotion()) { this.stage = 'done'; return; }
    const at = (ms, fn) => this.timers.push(setTimeout(fn, ms));
    // One frame empty first, so the liquid RISES rather than starting full.
    at(40, () => { this.stage = 'pour'; });
    at(POUR_MS, () => { this.stage = 'brew'; });
    at(POUR_MS + BREW_MS, () => { this.stage = 'show'; });
    this.shared.forEach((_, i) => at(POUR_MS + BREW_MS + EACH_MS * (i + 1), () => { this.count = i + 1; }));
    at(POUR_MS + BREW_MS + EACH_MS * (this.shared.length + 1), () => { this.stage = 'done'; });
  },
  beforeUnmount() { this.timers.forEach(clearTimeout); },
  methods: {
    skipOrClose() {
      if (this.stage !== 'done') { this.timers.forEach(clearTimeout); this.stage = 'done'; return; }
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.mix-reveal {
  width: min(420px, 100%); background: #14181f; color: #eef1f5;
  border: 2px solid #8f7ad6; border-radius: 14px; padding: 18px;
  display: flex; flex-direction: column; align-items: center; gap: 12px; text-align: center;
}
.mix-reveal--eureka { border-color: #d9a441; box-shadow: 0 0 40px rgba(217, 164, 65, 0.45); }
.mix-reveal__kind { align-self: flex-start; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.08em; color: #8f7ad6; font-weight: 800; }
// ---- the scene
.mx { position: relative; width: 240px; height: 170px; }
.mx__scene { position: absolute; inset: 0; width: 100%; height: 100%; overflow: visible; }
.mx__glass { fill: rgba(220, 235, 255, 0.06); stroke: #dfe7f1; stroke-width: 3; stroke-linejoin: round; }
.mx__lip { stroke: #dfe7f1; stroke-width: 4; stroke-linecap: round; }
.mx__shine { fill: none; stroke: rgba(255, 255, 255, 0.45); stroke-width: 3; stroke-linecap: round; }
.mx__names { position: absolute; left: 0; right: 0; bottom: -4px; font-weight: 800; font-size: 0.9rem; color: #c9d1db; }
// The liquid starts below the flask and rises as the two drop in.
.mx__liquid { transform: translateY(160px); transition: transform 0.9s cubic-bezier(0.3, 0.7, 0.3, 1) 0.55s; }
.mx--pour .mx__liquid, .mx--brew .mx__liquid, .mx--show .mx__liquid, .mx--done .mx__liquid { transform: translateY(104px); }
.mx__surface { animation: mx-sway 1.6s ease-in-out infinite alternate; }
@keyframes mx-sway { from { transform: translateX(-30px); } to { transform: translateX(0); } }
.mx--failed.mx--show .mx__surface, .mx--failed.mx--done .mx__surface { fill: #5d636b; transition: fill 0.4s; }
// Bubbles only while it works.
.mx__bubble { fill: rgba(255, 255, 255, 0.75); opacity: 0; }
.mx--brew .mx__bubble { animation: mx-bubble 1s ease-in infinite; animation-delay: calc(var(--i) * 0.17s); }
@keyframes mx-bubble { 0% { transform: translateY(0); opacity: 0; } 20% { opacity: 0.9; } 100% { transform: translateY(-62px); opacity: 0; } }
// A soft glow that breathes while it brews and flares on a eureka.
.mx__glow { opacity: 0; transform-origin: 120px 112px; transition: opacity 0.4s; }
.mx--brew .mx__glow { opacity: 1; animation: mx-breathe 0.8s ease-in-out infinite alternate; }
.mx--eureka.mx--show .mx__glow, .mx--eureka.mx--done .mx__glow { opacity: 1; animation: mx-flare 0.7s ease-out both; }
@keyframes mx-breathe { from { transform: scale(0.85); } to { transform: scale(1.05); } }
@keyframes mx-flare { 0% { transform: scale(0.6); } 60% { transform: scale(1.35); } 100% { transform: scale(1.1); } }
// Smoke from the neck when the bet fails.
.mx__smoke { fill: #8a9099; opacity: 0; }
.mx--failed.mx--show .mx__smoke, .mx--failed.mx--done .mx__smoke { animation: mx-smoke 1.4s ease-out both; animation-delay: calc(var(--i) * 0.15s); }
@keyframes mx-smoke { 0% { transform: translateY(0) scale(0.5); opacity: 0.8; } 100% { transform: translateY(-40px) scale(1.4); opacity: 0; } }
// The two elements: discs in their colour that arc over and drop in.
.mx__drop {
  position: absolute; top: 60px; width: 42px; height: 42px; margin-left: -21px; border-radius: 50%;
  display: grid; place-items: center; background: var(--c); box-shadow: 0 4px 14px rgba(0, 0, 0, 0.5);
  svg { width: 26px; height: 26px; fill: none; stroke: #101317; stroke-width: 1.9; }
}
.mx__drop--a { left: 30px; }
.mx__drop--b { left: 210px; }
.mx--pour .mx__drop--a { animation: mx-in-a 0.95s cubic-bezier(0.45, 0, 0.55, 1) both; }
.mx--pour .mx__drop--b { animation: mx-in-b 0.95s cubic-bezier(0.45, 0, 0.55, 1) 0.15s both; }
.mx--brew .mx__drop, .mx--show .mx__drop, .mx--done .mx__drop { opacity: 0; }
@keyframes mx-in-a { 0% { transform: translate(0, 0); } 55% { transform: translate(70px, -52px) scale(0.9); } 100% { transform: translate(90px, 18px) scale(0.3); opacity: 0; } }
@keyframes mx-in-b { 0% { transform: translate(0, 0); } 55% { transform: translate(-70px, -52px) scale(0.9); } 100% { transform: translate(-90px, 18px) scale(0.3); opacity: 0; } }
.mix-reveal__glass {
  width: 100%; min-height: 56px; border-radius: 12px; background: #1d2330;
  display: grid; place-items: center; padding: 10px;
}
.mix-reveal__wait { color: #9aa3ad; font-style: italic; }
.mix-reveal__badges { display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 8px; font-size: 1.15rem; }
.mix-reveal__both { font-weight: 800; }
.mix-reveal__badge { animation: mix-rise 0.45s ease-out both; }
@keyframes mix-rise { 0% { transform: translateY(26px) scale(0.4); opacity: 0; } 70% { transform: translateY(-4px) scale(1.2); } 100% { transform: none; opacity: 1; } }
@keyframes mix-pop { 0% { transform: scale(0.3); opacity: 0; } 70% { transform: scale(1.25); } 100% { transform: scale(1); opacity: 1; } }
.mix-reveal__said { font-size: 1.15rem; }
.mix-reveal__eureka {
  margin: 0; font-size: 1.35rem; font-weight: 900; color: #d9a441;
  animation: mix-pop 0.5s ease-out both;
}
.mix-reveal__note { margin: 0; font-size: 0.95rem; line-height: 1.4; color: #c9d1db; }
.mix-reveal__go { align-self: flex-end; }
@media (prefers-reduced-motion: reduce) {
  .mx__drop, .mx__liquid, .mx__surface, .mx__bubble, .mx__glow, .mx__smoke, .mix-reveal__badge, .mix-reveal__eureka { animation: none; transition: none; }
}
</style>
